<template>

  <div class="project-team-panel">

    <div class="project-team-columns">

      <!-- Column 1: Responsibilities -->
      <div class="project-team-column project-team-column--responsibilities">

        <div class="project-team-column-header">

          <h6 class="mb-0 font-weight-bold project-team-column-title">

            <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />

            Responsibilities

            <b-badge variant="light-secondary" pill class="ml-50">{{ responsibilityStepsGrouped.length }}</b-badge>

          </h6>

        </div>



        <div

          v-if="myResponsibilityStepCount"

          class="project-team-your-steps-header"

          aria-label="Steps assigned to you"

        >

          <feather-icon icon="UserCheckIcon" size="14" class="text-info flex-shrink-0" />

          <span class="project-team-your-steps-label">

            Your responsibilities ({{ myResponsibilityStepCount }}<template v-if="myOpenResponsibilityCount"> · {{ myOpenResponsibilityCount }} open</template>)

          </span>

        </div>






        <div

          v-if="!responsibilityStepsGrouped.length"

          class="project-team-empty project-team-empty--compact"

        >

          <feather-icon icon="CheckSquareIcon" size="24" class="project-team-empty-icon" />

          <p class="small mb-0">No responsibilities yet — add one to assign work.</p>

        </div>



        <ol v-else class="list-unstyled mb-0 project-team-steps">

          <li

            v-for="group in responsibilityStepsGrouped"

            :key="group.groupKey"

            class="project-team-step project-team-step--grouped"

            :class="{

              expanded: isGroupExpanded(group.groupKey),

              'is-done': group.allDone,

              'is-mine': !!myMemberInGroup(group),

            }"

          >

            <div class="project-team-step-rail" aria-hidden="true">

              <span class="project-team-step-badge">{{ group.stepNumber }}</span>

            </div>



            <div class="project-team-step-body project-team-member-card">

              <div class="project-team-step-top">

                <div class="project-team-step-identity min-width-0">

                  <div class="project-team-step-title-row">

                    <span class="project-team-step-label">Step {{ group.stepNumber }}</span>

                    <span class="project-team-step-sep" aria-hidden="true">·</span>

                    <span

                      class="project-team-step-responsibility-title"

                      :class="{ 'project-member-desc-done': group.allDone }"

                    >

                      {{ group.responsibilityTitle }}

                    </span>

                  </div>

                  <div class="project-team-step-meta project-team-step-assignees-row">

                    <span class="project-team-step-assignee-names">{{ group.assigneeNames }}</span>

                  </div>

                  <div

                    v-if="group.completionEntries.length"

                    class="project-team-shared-completion"

                    aria-label="Responsibility completion status"

                  >

                    <span class="project-team-completion-chips">

                      <!-- Solo assignee: status only (name already shown above) -->
                      <template v-if="group.completionEntries.length === 1">
                        <button
                          type="button"
                          class="project-team-completion-chip project-team-completion-chip--btn project-team-completion-chip--solo"
                          :class="group.completionEntries[0].completed ? 'is-done' : 'is-pending'"
                          :disabled="!canToggleResponsibility(group.completionEntries[0].empId) || saving"
                          :aria-label="canToggleResponsibility(group.completionEntries[0].empId)
                            ? `Mark ${group.completionEntries[0].name} ${group.completionEntries[0].completed ? 'pending' : 'done'}`
                            : (group.completionEntries[0].completed ? 'Done' : 'Pending')"
                          @click="toggleMemberCompletion(group.completionEntries[0].member, !group.completionEntries[0].completed)"
                        >
                          <feather-icon
                            v-if="group.completionEntries[0].completed"
                            icon="CheckIcon"
                            size="10"
                            class="project-team-completion-check"
                          />
                          <span v-else class="project-team-completion-circle" aria-hidden="true">○</span>
                          {{ group.completionEntries[0].completed ? 'Done' : 'Pending' }}
                        </button>
                      </template>

                      <!-- Shared step: keep per-person chips -->
                      <template v-else>
                        <span class="project-team-completion-label">Completed:</span>
                        <button
                          v-for="(entry, idx) in group.completionEntries"
                          :key="entry.empId"
                          type="button"
                          class="project-team-completion-chip project-team-completion-chip--btn"
                          :class="entry.completed ? 'is-done' : 'is-pending'"
                          :disabled="!canToggleResponsibility(entry.empId) || saving"
                          :aria-label="canToggleResponsibility(entry.empId) ? `Mark ${entry.name} ${entry.completed ? 'pending' : 'done'}` : entry.name"
                          @click="toggleMemberCompletion(entry.member, !entry.completed)"
                        >
                          <span v-if="idx > 0" class="project-team-completion-sep" aria-hidden="true">·</span>
                          {{ entry.name }}
                          <feather-icon
                            v-if="entry.completed"
                            icon="CheckIcon"
                            size="10"
                            class="project-team-completion-check"
                          />
                          <span v-else class="project-team-completion-circle" aria-hidden="true">○</span>
                        </button>
                      </template>

                    </span>

                  </div>

                </div>

                <div class="project-team-step-aside">

                  <div class="project-team-step-actions-row">

                    <div

                      v-if="myMemberInGroup(group) && group.completionEntries.length > 1"

                      class="project-member-responsibility-check project-member-responsibility-check--compact"

                    >

                      <b-form-checkbox

                        :checked="myMemberInGroup(group).responsibilityCompleted"

                        :disabled="!canToggleResponsibility(myMemberInGroup(group).empId) || saving"

                        class="project-member-done-checkbox"

                        @change="val => toggleResponsibility(myMemberInGroup(group), val)"

                      >

                        <span

                          class="project-member-done-label"

                          :class="{ 'is-complete': myMemberInGroup(group).responsibilityCompleted }"

                        >

                          {{ responsibilityCheckLabel(myMemberInGroup(group)) }}

                        </span>

                      </b-form-checkbox>

                    </div>

                    <div class="project-team-member-actions">

                      <span

                        class="project-attachment-count"

                        :class="{ 'is-empty': !group.attachments.length }"

                        :aria-label="group.attachments.length ? 'Reference documents' : 'No documents attached'"

                      >

                        <feather-icon icon="PaperclipIcon" size="12" />

                        {{ group.attachments.length }}

                      </span>

                      <b-button

                        v-if="canManage && hasValidMemberResponsibility(group)"

                        size="sm"

                        variant="link"

                        class="p-0 project-member-share-btn"

                        aria-label="Assign same responsibility to others"

                        @click="startShareResponsibility(group.primaryMember)"

                      >

                        <feather-icon icon="UsersIcon" size="13" />

                      </b-button>

                      <b-dropdown

                        v-if="canManage && !isCreator(group.primaryMember.empId)"

                        variant="link"

                        no-caret

                        toggle-class="p-0 project-member-menu-btn"

                        boundary="window"

                        :popper-opts="{ positionFixed: true }"

                        right

                      >

                        <template #button-content>

                          <feather-icon icon="MoreVerticalIcon" size="14" />

                        </template>

                        <b-dropdown-item @click="startEditMember(group.primaryMember)">

                          <feather-icon icon="EditIcon" size="13" class="mr-50" />

                          Edit member

                        </b-dropdown-item>

                        <b-dropdown-divider />

                        <b-dropdown-item variant="danger" @click="removeMember(group.primaryMember)">

                          Remove from project

                        </b-dropdown-item>

                      </b-dropdown>

                      <span

                        v-else

                        class="project-member-menu-placeholder"

                        aria-hidden="true"

                      />

                    </div>

                  </div>

                </div>

              </div>



              <div class="project-team-member-content">

                <button

                  v-if="hasExpandableGroupDetails(group)"

                  type="button"

                  class="project-member-details-toggle"

                  :class="{ expanded: isGroupExpanded(group.groupKey) }"

                  :aria-expanded="isGroupExpanded(group.groupKey) ? 'true' : 'false'"

                  @click="toggleGroupExpanded(group.groupKey)"

                >

                  <span>{{ isGroupExpanded(group.groupKey) ? 'Hide details' : 'View details' }}</span>

                  <feather-icon

                    :icon="isGroupExpanded(group.groupKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'"

                    size="13"

                  />

                </button>

                <div

                  v-if="sharingFromEmpId === group.primaryMember.empId"

                  class="project-share-responsibility-panel"

                >

                  <small class="text-muted d-block mb-25">Assign same responsibility to:</small>

                  <b-dropdown

                    variant="link"

                    toggle-class="form-control form-control-sm assignee-multiselect-toggle d-flex align-items-center justify-content-between"

                    block

                    no-caret

                    boundary="window"

                    :popper-opts="{ positionFixed: true }"

                    :disabled="!shareTargetOptions(group).length"

                    class="mb-50"

                  >

                    <template #button-content>

                      <span class="text-truncate">{{ shareTargetSummary(group) }}</span>

                      <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />

                    </template>

                    <div class="assignee-multiselect-menu" @click.stop>

                      <b-form-checkbox

                        v-for="option in shareTargetOptions(group)"

                        :key="option.empId"

                        v-model="shareTargetEmpIds"

                        :value="option.empId"

                        class="assignee-multiselect-item"

                      >

                        {{ option.label }}

                      </b-form-checkbox>

                    </div>

                  </b-dropdown>

                  <div v-if="shareTargetEmpIds.length" class="d-flex flex-wrap mb-50 project-selected-chips">

                    <b-badge

                      v-for="empId in shareTargetEmpIds"

                      :key="empId"

                      variant="light-primary"

                      pill

                      class="mr-25 mb-25 small"

                    >

                      {{ getAssigneeLabel(empId, shareTargetOptions(group)) }}

                    </b-badge>

                  </div>

                  <div class="d-flex" style="gap: 0.35rem;">

                    <b-button

                      size="sm"

                      variant="primary"

                      :disabled="!shareTargetEmpIds.length || saving"

                      @click="applyShareResponsibility(group.primaryMember)"

                    >

                      Assign

                    </b-button>

                    <b-button size="sm" variant="outline-secondary" @click="cancelShareResponsibility">

                      Cancel

                    </b-button>

                  </div>

                </div>

              </div>



              <div v-show="isGroupExpanded(group.groupKey)" class="project-member-details">

                <div v-if="groupHasDistinctDescription(group)" class="small mb-50">

                  <small class="text-muted d-block mb-25">What they need to do</small>

                  <p class="mb-0 text-body" :class="{ 'project-member-desc-done': group.allDone }">{{ group.responsibilityDescription }}</p>

                </div>

                <p v-else-if="hasValidNote(group.responsibilityTitle) && !groupHasDistinctDescription(group)" class="small mb-50 text-muted">

                  No additional details.

                </p>

                <p v-else class="small mb-50 project-member-no-note">

                  No responsibility note added.

                </p>



                <div v-if="group.attachments.length">

                  <small class="text-muted d-block mb-25">Reference documents</small>

                  <ul class="list-unstyled mb-0">

                    <li

                      v-for="att in group.attachments"

                      :key="att.id"

                      class="project-member-attachment-row d-flex align-items-center"

                    >

                      <feather-icon icon="FileTextIcon" size="13" class="text-muted mr-50 flex-shrink-0" />

                      <a

                        v-if="att.url"

                        :href="att.url"

                        target="_blank"

                        rel="noopener"

                        class="text-truncate small"

                        :download="att.name"

                        @click.stop

                      >

                        {{ att.name }}

                      </a>

                      <span v-else class="text-truncate small">{{ att.name }}</span>

                      <small v-if="att.size" class="text-muted ml-50 flex-shrink-0">{{ formatSize(att.size) }}</small>

                    </li>

                  </ul>

                </div>

                <p v-else-if="isGroupExpanded(group.groupKey)" class="small mb-0 text-muted project-member-no-attachments">

                  No reference documents attached.

                </p>

              </div>

            </div>

          </li>

        </ol>

      </div>



      <!-- Column 2: Roles / Team -->
      <div class="project-team-column project-team-column--roles">

        <div class="project-team-column-header">

          <h6 class="mb-0 font-weight-bold project-team-column-title">

            <feather-icon icon="UsersIcon" size="14" class="mr-50" />

            Team

            <b-badge variant="light-secondary" pill class="ml-50">{{ teamCount }}</b-badge>

          </h6>

        </div>



        <div v-if="!members.length" class="project-team-empty project-team-empty--compact">

          <feather-icon icon="UsersIcon" size="24" class="project-team-empty-icon" />

          <p class="small mb-0">No team members yet. Add people and assign roles.</p>

        </div>



        <div v-else class="project-team-roster">

          <ul class="list-unstyled mb-0 project-team-roster-list">

          <li

            v-for="member in teamRosterMembers"

            :key="'roster-' + member.empId"

            class="project-team-roster-item"

            :class="{
              'is-assigned': member.assignments.length,
              'is-unassigned': !member.assignments.length,
              'is-done': member.assignments.length && member.allAssignmentsDone,
              'is-mine': isAssignedToMe(member.empId),
            }"

          >

            <div class="project-team-roster-item-main">

              <b-avatar

                size="24"

                variant="light-primary"

                :text="avatarText(member.name)"

                class="flex-shrink-0"

              />

              <div class="project-team-roster-identity min-width-0">

                <div class="project-team-roster-title-row">

                  <span class="font-weight-bold text-truncate project-team-roster-name">{{ member.name }}</span>

                  <b-badge
                    v-if="isAssignedToMe(member.empId)"
                    variant="light-info"
                    pill
                    class="project-team-roster-you-badge"
                  >
                    You
                  </b-badge>

                  <project-role-badge :role="member.role" :short="true" />

                </div>

                <span
                  class="project-team-roster-assignment"
                  :class="{
                    'is-unassigned': !member.assignments.length,
                    'is-assigned': member.assignments.length && !member.allAssignmentsDone,
                    'is-done': member.assignments.length && member.allAssignmentsDone,
                  }"
                >
                  <template v-if="member.assignments.length">
                    <feather-icon
                      v-if="member.allAssignmentsDone"
                      icon="CheckIcon"
                      size="11"
                      class="project-team-roster-assignment-check"
                    />
                    <span class="project-team-roster-step-chips" aria-label="Assigned steps">
                      <span
                        v-for="item in member.assignments"
                        :key="'step-chip-' + member.empId + '-' + item.stepNumber"
                        class="project-team-roster-step-chip"
                        :class="{ 'is-done': item.completed }"
                      >{{ item.stepNumber }}</span>
                    </span>
                    <span class="project-team-roster-assignment-count">
                      {{ member.assignmentLabel }}
                    </span>
                  </template>
                  <template v-else>No assignment yet</template>
                </span>

              </div>

              <div class="project-team-roster-actions">

                <b-dropdown

                  v-if="canManage && !isCreator(member.empId)"

                  variant="link"

                  no-caret

                  toggle-class="p-0 project-member-menu-btn"

                  boundary="window"

                  :popper-opts="{ positionFixed: true }"

                  right

                >

                  <template #button-content>

                    <feather-icon icon="MoreVerticalIcon" size="14" />

                  </template>

                  <b-dropdown-item @click="startEditMember(member)">

                    <feather-icon icon="EditIcon" size="13" class="mr-50" />

                    Edit member

                  </b-dropdown-item>

                  <b-dropdown-divider />

                  <b-dropdown-item variant="danger" @click="removeMember(member)">

                    Remove from project

                  </b-dropdown-item>

                </b-dropdown>

              </div>

            </div>

            <b-card

              v-if="editingMemberId === member.empId"

              no-body

              class="p-1 border mt-50 project-member-form-card project-team-roster-edit"

            >

              <small class="text-muted d-block mb-50">Edit {{ member.name }}</small>

              <b-form-group label="Role" label-size="sm" class="mb-50">

                <b-form-select

                  v-model="editForm.role"

                  :options="editMemberRoleOptions"

                  value-field="key"

                  text-field="label"

                  size="sm"

                />

              </b-form-group>

              <p class="small text-muted mb-50">

                Assign work via <strong>Add responsibility</strong> in the left column.

              </p>

              <div class="project-member-form-footer mt-50">

                <b-button size="sm" variant="outline-secondary" @click="cancelEditMember">

                  Cancel

                </b-button>

                <b-button

                  size="sm"

                  variant="primary"

                  :disabled="saving"

                  @click="saveEditMember(member)"

                >

                  Save changes

                </b-button>

              </div>

            </b-card>

          </li>

          </ul>

        </div>

      </div>

    </div>



    <b-modal

      id="add-responsibility-modal"

      v-model="showAddResponsibility"

      title="Add responsibility"

      size="md"

      scrollable

      @hidden="resetAddResponsibilityForm"

    >

      <b-form @submit.prevent="addResponsibility">

        <b-form-group label="Title" label-for="new-responsibility-title">

          <b-form-input

            id="new-responsibility-title"

            v-model="newResponsibilityTitle"

            placeholder="e.g. Payroll module review"

            autofocus

          />

        </b-form-group>

        <b-form-group label="Description" label-for="new-responsibility-description">

          <b-form-textarea

            id="new-responsibility-description"

            v-model="newResponsibilityDescription"

            rows="3"

            placeholder="What needs to be done..."

          />

        </b-form-group>

        <b-form-group label="Assign to *" label-for="new-responsibility-assignees">

          <b-dropdown

            id="new-responsibility-assignees"

            variant="link"

            toggle-class="form-control assignee-multiselect-toggle d-flex align-items-center justify-content-between"

            block

            no-caret

            boundary="window"

            :popper-opts="{ positionFixed: true }"

            :disabled="!responsibilityAssigneeOptions.length"

          >

            <template #button-content>

              <span class="text-truncate">{{ responsibilityAssigneesSummary }}</span>

              <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />

            </template>

            <div class="assignee-multiselect-menu" @click.stop>

              <b-form-checkbox

                v-for="option in responsibilityAssigneeOptions"

                :key="option.empId"

                v-model="newResponsibilityAssignees"

                :value="option.empId"

                class="assignee-multiselect-item"

              >

                {{ option.label }}

              </b-form-checkbox>

            </div>

          </b-dropdown>

          <div v-if="newResponsibilityAssignees.length" class="d-flex flex-wrap mt-50 project-selected-chips">

            <b-badge

              v-for="empId in newResponsibilityAssignees"

              :key="empId"

              variant="light-primary"

              pill

              class="mr-25 mb-25"

            >

              {{ getAssigneeLabel(empId, responsibilityAssigneeOptions) }}

            </b-badge>

          </div>

        </b-form-group>

        <div v-if="showAddResponsibilityAttachments || newResponsibilityAttachments.length" class="mb-1">

          <b-form-group label-for="new-responsibility-attachments" class="mb-0">

            <template #label>

              <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />

              Reference documents

              <small class="text-muted font-weight-normal">(optional)</small>

            </template>

            <ul v-if="newResponsibilityAttachments.length" class="list-unstyled mb-50">

              <li

                v-for="(att, idx) in newResponsibilityAttachments"

                :key="att.id"

                class="project-member-attachment-row d-flex align-items-center"

              >

                <feather-icon icon="FileTextIcon" size="13" class="text-muted mr-50 flex-shrink-0" />

                <span class="text-truncate flex-grow-1 small">{{ att.name }}</span>

                <small v-if="att.size" class="text-muted mr-50 flex-shrink-0">{{ formatSize(att.size) }}</small>

                <b-button

                  size="sm"

                  variant="outline-danger"

                  class="px-50 py-0"

                  @click="removeNewResponsibilityAttachment(idx)"

                >

                  <feather-icon icon="XIcon" size="11" />

                </b-button>

              </li>

            </ul>

            <b-form-file

              id="new-responsibility-attachments"

              v-model="newResponsibilityFiles"

              :accept="attachmentAccept"

              multiple

              size="sm"

              placeholder="Choose files..."

              browse-text="Attach"

              @input="onNewResponsibilityFilesSelected"

            />

          </b-form-group>

        </div>

        <b-button

          v-else

          variant="link"

          class="p-0 add-responsibility-attach-toggle"

          @click="showAddResponsibilityAttachments = true"

        >

          <feather-icon icon="PaperclipIcon" size="13" class="mr-25" />

          Attach reference documents

        </b-button>

        <b-alert v-if="addResponsibilityFileErrors.length" show variant="danger" class="p-50 mt-1 mb-0 small">

          <div v-for="(err, i) in addResponsibilityFileErrors" :key="i">{{ err }}</div>

        </b-alert>

      </b-form>



      <template #modal-footer>

        <b-button variant="outline-secondary" @click="showAddResponsibility = false">

          Cancel

        </b-button>

        <b-button

          variant="primary"

          :disabled="!canSubmitAddResponsibility || saving || processingFiles"

          @click="addResponsibility"

        >

          Add responsibility

        </b-button>

      </template>

    </b-modal>



    <b-modal

      id="add-member-modal"

      v-model="showAddMember"

      title="Add team member"

      size="md"

      @hidden="resetAddForm"

    >

      <b-form-group label="Employees" label-for="new-member-employees">

        <b-dropdown

          id="new-member-employees"

          variant="link"

          toggle-class="form-control assignee-multiselect-toggle d-flex align-items-center justify-content-between"

          block

          no-caret

          boundary="window"

          :popper-opts="{ positionFixed: true }"

          :disabled="!availableUserOptions.length"

        >

          <template #button-content>

            <span class="text-truncate">{{ newMemberEmpIdsSummary }}</span>

            <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />

          </template>

          <div class="assignee-multiselect-menu" @click.stop>

            <b-form-checkbox

              v-for="option in availableUserOptions"

              :key="option.value"

              v-model="newMemberEmpIds"

              :value="option.value"

              class="assignee-multiselect-item"

            >

              {{ option.label }}

            </b-form-checkbox>

          </div>

        </b-dropdown>

        <div v-if="newMemberEmpIds.length" class="d-flex flex-wrap mt-50 project-selected-chips">

          <b-badge

            v-for="empId in newMemberEmpIds"

            :key="empId"

            variant="light-primary"

            pill

            class="mr-25 mb-25"

          >

            {{ getAssigneeLabel(empId, availableUserOptions) }}

          </b-badge>

        </div>

      </b-form-group>

      <b-form-group label="Role" label-for="new-member-role">

        <b-form-select

          id="new-member-role"

          v-model="newMemberRole"

          :options="addMemberRoleOptions"

          value-field="key"

          text-field="label"

          size="sm"

        />

      </b-form-group>

      <p class="small text-muted mb-0">

        They will appear in the team roster. Use <strong>Add responsibility</strong> to assign work later.

      </p>



      <template #modal-footer>

        <b-button variant="outline-secondary" @click="showAddMember = false">

          Cancel

        </b-button>

        <b-button

          variant="primary"

          :disabled="!newMemberEmpIds.length || saving"

          @click="addMember"

        >

          Add to team

        </b-button>

      </template>

    </b-modal>

  </div>

</template>



<script>

import {

  BAvatar, BBadge, BButton, BCard, BDropdown, BDropdownItem,

  BDropdownDivider, BForm, BFormGroup, BFormSelect, BFormTextarea, BFormInput, BFormFile, BAlert,
  BFormCheckbox, BModal,
} from 'bootstrap-vue'

import { avatarText } from '@core/utils/filter'

import ProjectRoleBadge from './ProjectRoleBadge.vue'

import {

  PROJECT_ROLES,

  PROJECT_MEMBER_ATTACHMENT_ACCEPT,

  normalizeMembers,

  reconcileProjectMembers,

  getResponsibilityGroupKey,

  projectMemberCount,

  formatMemberFileSize,

  filesToMemberAttachments,

  buildResponsibilitySteps,

  hasValidMemberResponsibility,

  responsibilityDescriptionDiffersFromTitle,

} from '@/utils/project-config'

import { buildResponsibilityStepsGrouped } from '@/utils/project-responsibility-tasks'

import { canManageProjectTeam, canToggleMemberResponsibility } from '@/utils/project-permissions'

import { getEmpId } from '@/utils/task-permissions'



export default {

  name: 'ProjectTeamPanel',

  components: {

    BAvatar, BBadge, BButton, BCard, BDropdown, BDropdownItem,

    BDropdownDivider, BForm, BFormGroup, BFormSelect, BFormTextarea, BFormInput, BFormFile, BAlert,
    BFormCheckbox, BModal,

    ProjectRoleBadge,

  },

  props: {

    project: { type: Object, required: true },

    currentUser: { type: Object, default: () => ({}) },

    assignableUsers: { type: Array, default: () => [] },

    saving: { type: Boolean, default: false },

  },

  data() {

    return {

      showAddMember: false,

      showAddResponsibility: false,

      showAddResponsibilityAttachments: false,

      newMemberEmpIds: [],

      newMemberRole: PROJECT_ROLES.CONTRIBUTOR,

      attachmentAccept: PROJECT_MEMBER_ATTACHMENT_ACCEPT,

      expandedGroupKeys: [],

      editingMemberId: null,

      editForm: {

        role: PROJECT_ROLES.CONTRIBUTOR,

      },

      processingFiles: false,

      sharingFromEmpId: null,

      shareTargetEmpIds: [],

      newResponsibilityTitle: '',

      newResponsibilityDescription: '',

      newResponsibilityAssignees: [],

      newResponsibilityAttachments: [],

      newResponsibilityFiles: null,

      addResponsibilityFileErrors: [],

    }

  },

  computed: {

    members() {

      return reconcileProjectMembers(this.project).members

    },

    /** All team members with assignment summary for the roster. */
    teamRosterMembers() {

      const assignmentsByEmpId = new Map()

      for (const group of this.responsibilityStepsGrouped) {

        for (const member of group.members) {

          const key = String(member.empId)

          if (!assignmentsByEmpId.has(key)) assignmentsByEmpId.set(key, [])

          assignmentsByEmpId.get(key).push({

            stepNumber: group.stepNumber,

            responsibilityTitle: group.responsibilityTitle,

            completed: !!member.responsibilityCompleted,

          })

        }

      }

      return this.members.map(member => {

        const assignments = (assignmentsByEmpId.get(String(member.empId)) || [])

          .slice()

          .sort((a, b) => a.stepNumber - b.stepNumber)

        return {

          ...member,

          assignments,

          assignment: assignments[0] || null,

          assignmentLabel: this.formatRosterAssignmentLabel(assignments),

          allAssignmentsDone: assignments.length > 0 && assignments.every(a => a.completed),

        }

      })

    },

    /**
     * Numbered responsibility steps from project.responsibilities (appends; not 1:1 with members).
     */
    responsibilitySteps() {
      return buildResponsibilitySteps(this.project)
    },

    /** One card per unique responsibility — grouped by group id or shared text. */
    responsibilityStepsGrouped() {
      return buildResponsibilityStepsGrouped(this.responsibilitySteps)
    },

    teamCount() {

      return projectMemberCount(this.project)

    },

    canManage() {

      return canManageProjectTeam(this.currentUser, this.project)

    },

    availableUsers() {

      const memberIds = new Set(this.members.map(m => String(m.empId)))

      const creator = this.project?.createdBy ?? this.project?.created_by

      if (creator != null) memberIds.add(String(creator))

      return (this.assignableUsers || [])

        .filter(u => {

          const id = String(u.emp_id ?? u.empId ?? u.id)

          return !memberIds.has(id)

        })

        .map(u => ({

          ...u,

          emp_id: u.emp_id ?? u.empId ?? u.id,

          label: u.first_name

            ? `${u.first_name} ${u.last_name || ''}`.trim()

            : (u.name || u.title || String(u.emp_id)),

        }))

    },

    availableUserOptions() {

      return this.availableUsers.map(u => ({

        value: String(u.emp_id),

        label: u.label,

      }))

    },

    newMemberEmpIdsSummary() {

      if (!this.newMemberEmpIds.length) return 'Select employees…'

      const names = this.newMemberEmpIds.map(id => this.getAssigneeLabel(id, this.availableUserOptions))

      if (names.length <= 2) return names.join(', ')

      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`

    },

    responsibilityAssigneesSummary() {

      if (!this.newResponsibilityAssignees.length) return 'Select team members…'

      const names = this.newResponsibilityAssignees.map(id => this.getAssigneeLabel(id, this.responsibilityAssigneeOptions))

      if (names.length <= 2) return names.join(', ')

      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`

    },

    currentEmpId() {

      return getEmpId(this.currentUser)

    },

    myResponsibilityStepCount() {
      if (!this.currentEmpId) return 0
      return this.responsibilityStepsGrouped.filter(
        group => group.members.some(m => String(m.empId) === String(this.currentEmpId)),
      ).length
    },

    myOpenResponsibilityCount() {
      if (!this.currentEmpId) return 0
      return this.responsibilityStepsGrouped.filter(group => {
        const mine = group.members.find(m => String(m.empId) === String(this.currentEmpId))
        return mine && !mine.responsibilityCompleted
      }).length
    },

    /** PM + Contributor only; PM hidden when another PM already exists. */
    addMemberRoleOptions() {

      return this.teamMemberRoleOptions()

    },

    editMemberRoleOptions() {

      return this.teamMemberRoleOptions(this.editingMemberId)

    },

    responsibilityAssigneeOptions() {

      const assignedEmpIds = new Set(

        this.responsibilitySteps.map(step => String(step.empId)),

      )

      return this.members

        .map(m => {

          const empId = String(m.empId)

          if (!empId) return null

          const name = m.name || empId

          return {

            empId,

            label: assignedEmpIds.has(empId)

              ? `${name} (already assigned)`

              : name,

          }

        })

        .filter(Boolean)

        .sort((a, b) => a.label.localeCompare(b.label))

    },

    canSubmitAddResponsibility() {

      return this.hasValidNote(this.newResponsibilityTitle)

        && this.newResponsibilityAssignees.length > 0

    },

  },

  methods: {

    avatarText,

    formatSize: formatMemberFileSize,

    getAssigneeLabel(empId, options) {

      const id = String(empId)

      const match = (options || []).find(option => String(option.empId ?? option.value) === id)

      if (match) return match.label

      return this.members.find(m => String(m.empId) === id)?.name || id

    },

    shareTargetSummary(group) {

      if (!this.shareTargetEmpIds.length) return 'Select team members…'

      const options = this.shareTargetOptions(group)

      const names = this.shareTargetEmpIds.map(id => this.getAssigneeLabel(id, options))

      if (names.length <= 2) return names.join(', ')

      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`

    },

    hasValidNote(description) {

      if (!description) return false

      const trimmed = String(description).trim()

      if (!trimmed) return false

      return trimmed.toUpperCase() !== 'NA' && trimmed.toUpperCase() !== 'N/A'

    },

    hasValidMemberResponsibility(memberOrGroup) {
      if (memberOrGroup?.members) {
        return hasValidMemberResponsibility(memberOrGroup.primaryMember || memberOrGroup.members[0])
      }
      return hasValidMemberResponsibility(memberOrGroup)
    },

    groupHasDistinctDescription(group) {
      return this.hasValidNote(group.responsibilityDescription)
        && responsibilityDescriptionDiffersFromTitle(
          group.responsibilityTitle,
          group.responsibilityDescription,
        )
    },

    /** Compact roster label — step refs only; full titles live in Responsibilities. */
    formatRosterAssignmentLabel(assignments) {

      if (!assignments?.length) return null

      if (assignments.length === 1) return '1 assignment'

      return `${assignments.length} assignments`

    },

    isCreator(empId) {

      const creator = this.project?.createdBy ?? this.project?.created_by

      return creator != null && String(creator) === String(empId)

    },

    isAssignedToMe(empId) {

      return this.currentEmpId != null && String(empId) === String(this.currentEmpId)

    },

    isGroupExpanded(groupKey) {
      return this.expandedGroupKeys.includes(String(groupKey))
    },

    toggleGroupExpanded(groupKey) {
      const key = String(groupKey)
      if (this.isGroupExpanded(key)) {
        this.expandedGroupKeys = this.expandedGroupKeys.filter(x => x !== key)
      } else {
        this.expandedGroupKeys = [...this.expandedGroupKeys, key]
      }
    },

    myMemberInGroup(group) {
      if (!this.currentEmpId || !group?.members) return null
      return group.members.find(m => String(m.empId) === String(this.currentEmpId)) || null
    },

    hasExpandableGroupDetails(group) {
      return this.groupHasDistinctDescription(group)
        || (group.attachments && group.attachments.length)
    },

    toggleMemberCompletion(member, checked) {
      if (!this.canToggleResponsibility(member.empId)) return
      this.toggleResponsibility(member, checked)
    },

    toggleAddMember() {

      if (this.showAddMember) {

        this.showAddMember = false

        return

      }

      this.showAddMember = true

      this.showAddResponsibility = false

      if (!this.addMemberRoleOptions.some(o => o.key === this.newMemberRole)) {

        this.newMemberRole = PROJECT_ROLES.CONTRIBUTOR

      }

    },

    toggleAddResponsibility() {

      if (this.showAddResponsibility) {

        this.showAddResponsibility = false

        return

      }

      this.showAddResponsibility = true

      this.showAddMember = false

      this.showAddResponsibilityAttachments = false

    },

    resetAddResponsibilityForm() {

      this.newResponsibilityTitle = ''

      this.newResponsibilityDescription = ''

      this.newResponsibilityAssignees = []

      this.newResponsibilityAttachments = []

      this.newResponsibilityFiles = null

      this.addResponsibilityFileErrors = []

      this.showAddResponsibilityAttachments = false

    },

    removeNewResponsibilityAttachment(idx) {

      this.newResponsibilityAttachments.splice(idx, 1)

    },

    async onNewResponsibilityFilesSelected(files) {

      if (!files || (Array.isArray(files) && !files.length)) return

      await this.appendFilesToPending(files)

      this.newResponsibilityFiles = null

    },

    buildMemberDefaultsForEmpIds(empIds) {
      const defaults = {}
      const memberById = new Map(this.members.map(m => [String(m.empId), m]))
      for (const rawId of empIds || []) {
        const empId = String(rawId)
        if (defaults[empId]) continue
        const member = memberById.get(empId)
        if (member) {
          defaults[empId] = {
            name: member.name,
            email: member.email,
            role: member.role,
          }
          continue
        }
        const user = (this.assignableUsers || []).find(
          u => String(u.emp_id ?? u.empId ?? u.id) === empId,
        )
        if (!user) continue
        defaults[empId] = {
          name: user.first_name
            ? `${user.first_name} ${user.last_name || ''}`.trim()
            : (user.name || user.title || empId),
          email: user.email || null,
          role: PROJECT_ROLES.CONTRIBUTOR,
          addedBy: this.currentEmpId,
        }
      }
      return defaults
    },

    addResponsibility() {

      if (!this.canSubmitAddResponsibility) return

      const targetEmpIds = [...this.newResponsibilityAssignees]
      const memberDefaults = this.buildMemberDefaultsForEmpIds(targetEmpIds)

      this.$emit('add-responsibility', {

        targetEmpIds,

        memberDefaults,

        responsibilityTitle: (this.newResponsibilityTitle || '').trim(),

        responsibilityDescription: (this.newResponsibilityDescription || '').trim(),

        attachments: [...this.newResponsibilityAttachments],

      })

      this.resetAddResponsibilityForm()

      this.showAddResponsibility = false

    },

    resetAddForm() {

      this.newMemberEmpIds = []

      this.newMemberRole = PROJECT_ROLES.CONTRIBUTOR

    },

    teamMemberRoleOptions(excludeEmpId = null) {

      const base = [

        { key: PROJECT_ROLES.PROJECT_MANAGER, label: 'Project Manager' },

        { key: PROJECT_ROLES.CONTRIBUTOR, label: 'Contributor' },

      ]

      const hasOtherPm = this.members.some(m => (

        m.role === PROJECT_ROLES.PROJECT_MANAGER

        && String(m.empId) !== String(excludeEmpId)

      ))

      if (hasOtherPm) {

        return base.filter(r => r.key === PROJECT_ROLES.CONTRIBUTOR)

      }

      return base

    },

    /** Emp ids already on this responsibility (group members, or solo assignee). */
    responsibilityAssignExcludeIds(empIdOrGroup) {
      const excludeIds = new Set()
      if (empIdOrGroup?.members?.length) {
        empIdOrGroup.members.forEach(m => excludeIds.add(String(m.empId)))
        return excludeIds
      }
      const empId = empIdOrGroup?.empId ?? empIdOrGroup
      if (empId == null) return excludeIds
      excludeIds.add(String(empId))
      const member = this.members.find(m => String(m.empId) === String(empId))
      const groupKey = member ? getResponsibilityGroupKey(member) : null
      if (groupKey) {
        this.members.forEach(m => {
          if (getResponsibilityGroupKey(m) === groupKey) excludeIds.add(String(m.empId))
        })
      }
      return excludeIds
    },

    shareTargetOptions(group) {
      const excludeIds = this.responsibilityAssignExcludeIds(group)
      return this.members
        .filter(m => m.empId != null && !excludeIds.has(String(m.empId)))
        .map(m => ({ empId: m.empId, label: m.name }))
    },

    startShareResponsibility(member) {

      this.sharingFromEmpId = member.empId

      this.shareTargetEmpIds = []

      this.editingMemberId = null

    },

    cancelShareResponsibility() {

      this.sharingFromEmpId = null

      this.shareTargetEmpIds = []

    },

    applyShareResponsibility(member) {

      if (!this.shareTargetEmpIds.length) return

      this.$emit('assign-responsibility', {

        sourceEmpId: member.empId,

        targetEmpIds: [...this.shareTargetEmpIds],

      })

      this.cancelShareResponsibility()

    },

    async appendFilesToPending(files) {

      this.processingFiles = true

      try {

        const { attachments, errors } = await filesToMemberAttachments(files, {

          uploadedBy: this.currentEmpId,

        })

        this.newResponsibilityAttachments = [...this.newResponsibilityAttachments, ...attachments]

        this.addResponsibilityFileErrors = errors

      } finally {

        this.processingFiles = false

      }

    },

    addMember() {

      if (!this.newMemberEmpIds.length) return

      for (const rawId of this.newMemberEmpIds) {

        const empId = String(rawId)

        const user = this.availableUsers.find(u => String(u.emp_id) === empId)

          || (this.assignableUsers || []).find(

            u => String(u.emp_id ?? u.empId ?? u.id) === empId,

          )

        this.$emit('add-member', {

          empId,

          name: user?.label

            || (user?.first_name

              ? `${user.first_name} ${user.last_name || ''}`.trim()

              : (user?.name || user?.title || empId)),

          email: user?.email || null,

          role: this.newMemberRole,

        })

      }

      this.resetAddForm()

      this.showAddMember = false

    },

    startEditMember(member) {

      this.editingMemberId = member.empId

      this.editForm = {

        role: member.role,

      }

      this.cancelShareResponsibility()

    },

    cancelEditMember() {

      this.editingMemberId = null

      this.editForm = {

        role: PROJECT_ROLES.CONTRIBUTOR,

      }

    },

    saveEditMember(member) {

      this.$emit('update-member', {

        ...member,

        role: this.editForm.role,

      })

      this.cancelEditMember()

    },

    removeMember(member) {

      this.$emit('remove-member', member)

    },

    canToggleResponsibility(memberEmpId) {

      return canToggleMemberResponsibility(this.currentUser, this.project, memberEmpId)

    },

    responsibilityCheckLabel(member) {

      if (member.responsibilityCompleted) {

        return String(member.empId) === String(this.currentEmpId)

          ? 'My responsibilities are done'

          : 'Responsibilities completed'

      }

      return String(member.empId) === String(this.currentEmpId)

        ? 'Mark my responsibilities as done'

        : 'Mark responsibilities as done'

    },

    toggleResponsibility(member, checked) {

      if (!this.canToggleResponsibility(member.empId)) return

      this.$emit('update-member', {

        ...member,

        responsibilityCompleted: !!checked,

        responsibilityCompletedAt: checked ? new Date().toISOString() : null,

      })

    },

  },

}

</script>



<style lang="scss">

@import '~@core/scss/vue/apps/task.scss';

</style>



<style lang="scss" scoped>

.assignee-multiselect-toggle {

  width: 100%;

  font-weight: 400;

  text-align: left;

  text-decoration: none !important;



  &:hover,

  &:focus,

  &:active {

    text-decoration: none !important;

  }

}



.assignee-multiselect-menu {

  max-height: 220px;

  overflow-y: auto;

  padding: 0.5rem 0;

  min-width: 220px;

  .assignee-multiselect-item.custom-control {

    padding: 0.25rem 0.5rem 0.25rem 2.1rem;

    margin-bottom: 0;

    min-height: auto;

    width: 100%;

  }

  .assignee-multiselect-item .custom-control-label {

    cursor: pointer;

    line-height: 1.35;

  }

}



.project-selected-chips {

  gap: 0.25rem;

}



.min-width-0 {

  min-width: 0;

}

.project-member-desc-done {

  text-decoration: line-through;

  opacity: 0.75;

}

.project-member-done-label {

  display: inline-flex;

  align-items: center;

  font-size: 0.875rem;

  line-height: 1.3;

  &.is-complete {

    color: #28c76f;

    font-weight: 500;

  }

}

</style>

